/**
 * Neural layout engine.
 *
 * No start corner, no left→right. What matters is clustering and spacing:
 *   1. Islands. Same primary tag lives together as a compact blob. Nested
 *      missions inherit their root hub's tag. Untagged roots share one island
 *      too — packed like any other nebula, just without smoke.
 *   2. Inside an island: organic force layout — even gaps, short wires stay short,
 *      nothing overlaps. A hub reserves a disk the size of its nest.
 *   3. Islands sit snug in the window's aspect. Cross-tag wires pull those
 *      nebulas onto neighbouring seats so handoffs do not spaghetti.
 *   4. Nested campaigns ring their hub, evenly around it.
 *
 * Deterministic (seeded by graph structure). Output is world units, y up.
 */

import { assignClusters } from './clusters';
import { CLUSTER_TITLE_BAND, mulberry32, UNTAGGED_KEY } from './constants';
import { computeDepthFromForward, computeDepthToLeaf } from './depth';
import { layoutIsland, nodeRadius, podRadius } from './island';
import { nestOverflow, separateSameIslandNests } from './nests';
import { buildIslandLinks, packCircles } from './pack';

export { MIN_ISLAND_MEMBERS, UNTAGGED_KEY, CLUSTER_TITLE_BAND, LAYOUT_REVISION, quantizeLayoutAspect } from './constants';
export { computeDepthFromForward, computeDepthToLeaf } from './depth';
export { assignClusters } from './clusters';
export { podRadius } from './island';
export { nestFootprint } from './nests';

/**
 * @param {{
 *   units: Array<{ id: string, task: any, hubIndex: number|null, depth: number }>,
 *   depEdges: Array<[number, number]>,
 *   nestSprings: Array<[number, number]>,
 *   clusterKeyOf: (task: any) => string,
 *   seed: number,
 *   aspect?: number,
 * }} input
 */
export function computeLayout({ units, depEdges, nestSprings, clusterKeyOf, seed, aspect = 1.6 }) {
  const N = units.length;
  const rnd = mulberry32(seed ^ 0x9e3779b9);

  const forward = Array.from({ length: N }, () => []);
  const reverse = Array.from({ length: N }, () => []);
  depEdges.forEach(([j, i]) => { forward[i].push(j); reverse[j].push(i); });
  const childCount = new Array(N).fill(0);
  const childrenOf = Array.from({ length: N }, () => []);
  nestSprings.forEach(([hub, i]) => { childCount[hub] += 1; childrenOf[hub].push(i); });

  const depthFromRoot = computeDepthFromForward(N, forward);
  const depthToLeaf = computeDepthToLeaf(N, reverse);
  const flow = new Float64Array(N);
  for (let i = 0; i < N; i += 1) {
    const fr = depthFromRoot[i];
    const tl = depthToLeaf[i];
    flow[i] = (fr === 0 && tl === 0) ? 0.5 : fr / (fr + tl);
  }

  const { clusterKeys, clusterIndex, clusterMembers } = assignClusters(units, clusterKeyOf);
  const C = clusterKeys.length;
  const roots = [];
  for (let i = 0; i < N; i += 1) if (units[i].hubIndex === null) roots.push(i);

  const size = new Float64Array(N);
  for (let i = 0; i < N; i += 1) {
    const u = units[i];
    if (u.hubIndex !== null) {
      size[i] = u.depth >= 2 ? 0.42 : 0.55;
      continue;
    }
    const deg = reverse[i].length + forward[i].length * 0.5 + childCount[i] * 0.35;
    let s = 0.85 + Math.log2(1 + deg) * 0.42;
    if (childCount[i] > 0) s = Math.max(s, 1.45);
    size[i] = Math.min(2.4, s);
  }

  const podR = new Float64Array(N);
  for (let i = 0; i < N; i += 1) {
    if (!childCount[i]) continue;
    let r = podRadius(childCount[i], units[i].depth + 1);
    childrenOf[i].forEach((c) => { if (childCount[c]) r += podRadius(childCount[c], 2) * 0.45; });
    podR[i] = r;
  }

  const rad = new Map();
  roots.forEach((i) => rad.set(i, nodeRadius(i, size, childCount, podR)));

  const x = new Float64Array(N);
  const y = new Float64Array(N);
  const islandR = new Float64Array(C);
  for (let c = 0; c < C; c += 1) {
    const rs = clusterMembers[c].filter((i) => units[i].hubIndex === null);
    const hull = layoutIsland({ rs, x, y, rad, edges: depEdges, rnd });
    islandR[c] = hull + nestOverflow(rs, childCount, podR, x, y, hull)
      + (clusterKeys[c] === UNTAGGED_KEY ? 0 : CLUSTER_TITLE_BAND);
  }

  const packed = packCircles(
    Array.from(islandR),
    rnd,
    aspect,
    buildIslandLinks(C, clusterIndex, depEdges),
  );
  roots.forEach((i) => {
    const c = clusterIndex[i];
    x[i] += packed.cx[c];
    y[i] += packed.cy[c];
  });

  // Global breathing room after islands are placed — leftover touches between
  // a hub's reserved disk and a neighbour get resolved without breaking the blobs.
  for (let pass = 0; pass < 20; pass += 1) {
    let moved = false;
    for (let a = 0; a < roots.length; a += 1) {
      const i = roots[a];
      for (let b = a + 1; b < roots.length; b += 1) {
        const j = roots[b];
        let dx = x[i] - x[j]; let dy = y[i] - y[j];
        let dist = Math.hypot(dx, dy);
        const need = rad.get(i) + rad.get(j) + 0.62;
        if (dist < 1e-4) { dx = 1; dy = 0; dist = 1; }
        if (dist >= need) continue;
        moved = true;
        const push = (need - dist) * 0.5;
        const ux = dx / dist; const uy = dy / dist;
        x[i] += ux * push; y[i] += uy * push;
        x[j] -= ux * push; y[j] -= uy * push;
      }
    }
    if (!moved) break;
  }

  const hubs = [];
  for (let i = 0; i < N; i += 1) if (childCount[i]) hubs.push(i);
  hubs.sort((a, b) => units[a].depth - units[b].depth);
  hubs.forEach((h) => {
    const kids = childrenOf[h];
    if (!kids.length) return;
    const kidRad = new Map();
    kids.forEach((i) => {
      kidRad.set(i, childCount[i] ? 0.42 + podR[i] * 0.22 : 0.3);
    });
    // Same engine as a tag island: real inner wires decide the shape.
    layoutIsland({ rs: kids, x, y, rad: kidRad, edges: depEdges, rnd });
    kids.forEach((i) => { x[i] += x[h]; y[i] += y[h]; });
  });
  separateSameIslandNests({
    hubs, childrenOf, clusterIndex, childCount, x, y, depthOf: (h) => units[h].depth,
  });

  let sx = 0; let sy = 0; let sw = 0;
  roots.forEach((i) => { sx += x[i] * size[i]; sy += y[i] * size[i]; sw += size[i]; });
  if (sw > 0) { sx /= sw; sy /= sw; }
  for (let i = 0; i < N; i += 1) { x[i] -= sx; y[i] -= sy; }

  const clusterBoxes = clusterMembers.map((m) => {
    const rs = m.filter((i) => units[i].hubIndex === null);
    if (!rs.length) return { cx: 0, cy: 0, halfW: 1.5, halfH: 1.5 };
    let cx = 0; let cy = 0;
    rs.forEach((i) => { cx += x[i]; cy += y[i]; });
    cx /= rs.length; cy /= rs.length;
    let r = 1.6;
    m.forEach((i) => {
      const extra = units[i].hubIndex === null ? (rad.get(i) * 0.4) : 0.5;
      r = Math.max(r, Math.hypot(x[i] - cx, y[i] - cy) + extra);
    });
    return { cx, cy, halfW: r, halfH: r };
  });
  const podBoxes = new Map();
  hubs.forEach((h) => {
    let r = 1.1;
    childrenOf[h].forEach((i) => {
      r = Math.max(r, Math.hypot(x[i] - x[h], y[i] - y[h]) + 0.45);
    });
    podBoxes.set(h, { cx: x[h], cy: y[h], halfW: r, halfH: r });
  });

  return {
    x,
    y,
    size,
    flow,
    clusterIndex,
    clusterKeys,
    clusterMembers,
    clusterBoxes,
    podBoxes,
    childCount,
    degree: reverse.map((r, i) => r.length + forward[i].length),
  };
}
